import axiosClient from './axiosClient';
import { SyncResult } from './syncPlatformApi';

/* ── Global catalog drift ──────────────────────────────────────────────── */

export interface CatalogDrift {
  key: string;
  label: string;
  strategy: 'full-mirror' | 'referenced';
  /** All non-deleted platform rows of this type. */
  platformTotal: number;
  /** Rows currently in the config mirror. */
  configTotal: number;
  /** Syncable rows: on platform (per strategy) but missing from config. */
  missing: number;
  note: string;
}

export interface CatalogSyncResult {
  key: string;
  label: string;
  synced: number;
}

export interface GlobalSyncResponse {
  synced: CatalogSyncResult[];
  catalogs: CatalogDrift[];
}

export const globalDriftApi = {
  /** Read-only per-catalog drift (platform vs config mirror). */
  get: async (): Promise<CatalogDrift[]> => {
    const { data } = await axiosClient.get('/api/admin/globals/drift');
    return data.catalogs as CatalogDrift[];
  },

  /** Reconcile selected catalogs (or all when omitted); returns synced + fresh drift. */
  sync: async (catalogs?: string[]): Promise<GlobalSyncResponse> => {
    const { data } = await axiosClient.post(
      '/api/admin/globals/drift/sync',
      catalogs && catalogs.length ? { catalogs } : {},
    );
    return data as GlobalSyncResponse;
  },
};

/* ── Per-tenant drift (reuses the SyncResult shape) ────────────────────── */

export const tenantDriftApi = {
  /** Dry-run drift for a tenant already in config (importedRows = drift). */
  get: async (tenantCode: string): Promise<SyncResult> => {
    const { data } = await axiosClient.get(`/api/tenant-drift/${tenantCode}`);
    return data as SyncResult;
  },

  /** Apply the drift (pull missing platform rows into config). */
  sync: async (tenantCode: string): Promise<SyncResult> => {
    const { data } = await axiosClient.post(`/api/tenant-drift/${tenantCode}/sync`);
    return data as SyncResult;
  },
};
